import type { DexieOptions, Table } from "dexie";
import { Dexie } from "dexie";
import { migrateProductIds } from "./migrations/productIds";
import type { ComboEntry, GroupEntry, MetaEntry, ProductEntry } from "./types";

const stores = {
	groups: "[orgId+id], orgId, updatedAt",
	products: "[orgId+id], orgId, updatedAt",
	combos: "[orgId+id], orgId, updatedAt",
	meta: "[orgId+key]",
};

export class InnocloudsDB extends Dexie {
	groups!: Table<GroupEntry, [GroupEntry["orgId"], GroupEntry["id"]]>;
	products!: Table<ProductEntry, [ProductEntry["orgId"], ProductEntry["id"]]>;
	combos!: Table<ComboEntry, [ComboEntry["orgId"], ComboEntry["id"]]>;
	meta!: Table<MetaEntry, [MetaEntry["orgId"], MetaEntry["key"]]>;

	constructor(dbName: string, options?: DexieOptions) {
		super(dbName, options);
		this.version(1).stores(stores);
		this.on("ready", (vip) => migrateProductIds(vip));
	}
}

export interface PreviewFrame {
	tile: string | null;
	session: string | null;
	scope: string;
}

const PREVIEW_FRAME_NAME = /^inno-preview-app(?::([^@]+))?(?:@(.+))?$/;

const detectPreviewFrame = (): PreviewFrame | null => {
	const view = (globalThis as { window?: { parent: unknown; name: string } }).window;
	if (!view) return null;
	try {
		if (view.parent === view) return null;
		const match = PREVIEW_FRAME_NAME.exec(view.name);
		if (!match) return null;
		const tile = match[1] ?? null;
		return { tile, session: match[2] ?? null, scope: `${import.meta.env.BASE_URL}${tile ?? "main"}` };
	} catch {
		return null;
	}
};

export const previewFrame = detectPreviewFrame();

export const PREVIEW_DB_PREFIX = "innoclouds-preview";

const previewDbName = (frame: PreviewFrame) => `${PREVIEW_DB_PREFIX}:${frame.scope}${frame.session ? `@${frame.session}` : ""}`;

/** Dev-режим с моками (`VITE_MOCK_API=true`): кроме точки, меню, дизайна и decoration запросы отвечает @innoclouds/mock */
export const mockApi = import.meta.env.DEV && !previewFrame && import.meta.env.VITE_MOCK_API === "true";

const MOCK_DB_NAME = "innoclouds-mock";

export const db = new InnocloudsDB(previewFrame ? previewDbName(previewFrame) : mockApi ? MOCK_DB_NAME : "innoclouds");

export const withPreviewScope = (name: string) => (previewFrame || mockApi ? `${name}|${db.name}` : name);
